import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useUser } from '../hooks/useUser.ts';
import { apiFetch } from '../lib/api.ts';

type Decision = 'proceed_close' | 'hold' | 'proceed_backorder';

interface ShortageRow {
  do_no: string;
  status: string;
  ground_plan_qty: number;
  ground_scanned_qty: number;
  lorry_plan_qty: number;
  lorry_scanned_qty: number;
  held_reason: string | null;
  declared_short: number;
}

const DECISIONS: Array<{ key: Decision; label: string; hint: string }> = [
  {
    key: 'proceed_close',
    label: 'Proceed & close',
    hint: 'Accept the scanned quantity and carry on. The shortfall is written off.',
  },
  {
    key: 'proceed_backorder',
    label: 'Proceed & backorder',
    hint: 'Carry on, and create a new Delivery Order in OBM for the shortfall.',
  },
  {
    key: 'hold',
    label: 'Hold',
    hint: 'Stop this dispatch. Nothing further happens until someone picks it up.',
  },
];

export function DispatchShortagesPage() {
  const qc = useQueryClient();
  const { user } = useUser();
  // Mirrors ProtectedRoute: a session predating permissions sees everything,
  // and the server enforces the same key regardless of what we render here.
  const perms = (user?.permissions as string[] | undefined) ?? null;
  const canDecide = perms === null || perms.includes('write_tasks.approve');

  const q = useQuery({
    queryKey: ['dispatch-shortages'],
    queryFn: () => apiFetch<ShortageRow[]>('/api/admin/dispatch/shortages'),
    refetchInterval: 30_000,
  });

  const decide = useMutation({
    mutationFn: ({ doNo, decision }: { doNo: string; decision: Decision }) =>
      apiFetch(`/api/admin/dispatch/${encodeURIComponent(doNo)}/decision`, {
        method: 'POST',
        body: JSON.stringify({ decision }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dispatch-shortages'] }),
  });

  const isGround = (r: ShortageRow) => r.status.startsWith('ground');
  const stageOf = (r: ShortageRow) =>
    isGround(r) ? 'Ground' : r.status.startsWith('lorry') ? 'Lorry' : '—';
  const plannedOf = (r: ShortageRow) => (isGround(r) ? r.ground_plan_qty : r.lorry_plan_qty);
  const scannedOf = (r: ShortageRow) => (isGround(r) ? r.ground_scanned_qty : r.lorry_scanned_qty);

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Dispatch exceptions</h1>
        <span className="main-subtitle">
          {q.data ? `${q.data.length} awaiting a decision` : ''}
        </span>
      </header>

      {q.error && <div className="error">{(q.error as Error).message}</div>}
      {decide.error && <div className="error">{(decide.error as Error).message}</div>}
      {q.isLoading && <div className="loading">Loading…</div>}
      {q.data && q.data.length === 0 && (
        <p className="empty">
          Nothing outstanding. A delivery appears here when a scan comes up short, or when it
          cannot be invoiced.
        </p>
      )}

      {q.data && q.data.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>DO</th>
              <th>Stage</th>
              <th className="num">Planned</th>
              <th className="num">Scanned</th>
              <th className="num">Short by</th>
              <th>What happened</th>
              <th>Decision</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((r) => {
              const held = r.status === 'held';
              const scanShort = r.status.endsWith('_shortage_pending');
              const declaredShort = Number(r.declared_short) || 0;
              const short = Math.max(0, plannedOf(r) - scannedOf(r));
              return (
                <tr key={r.do_no}>
                  <td className="mono">
                    <Link to={`/delivery-orders/${encodeURIComponent(r.do_no)}`}>{r.do_no}</Link>
                  </td>
                  <td>{scanShort ? stageOf(r) : '—'}</td>
                  <td className="num">{scanShort ? plannedOf(r) : '—'}</td>
                  <td className="num">{scanShort ? scannedOf(r) : '—'}</td>
                  <td className="num">{scanShort ? short : '—'}</td>
                  <td>
                    {held && (
                      <>
                        <span className="badge badge-danger">held</span>{' '}
                        {r.held_reason ?? 'Held — no reason recorded.'}
                      </>
                    )}
                    {scanShort && (
                      <span className="badge badge-pending">{stageOf(r).toLowerCase()} short</span>
                    )}
                    {declaredShort > 0 && (
                      <>
                        {' '}
                        <span className="badge badge-neutral">declared short</span>{' '}
                        {declaredShort} pcs of a non-RFID line were not shipped.
                      </>
                    )}
                  </td>
                  <td>
                    {held || !scanShort ? (
                      // A held dispatch is a data problem, not a quantity call — the
                      // decisions below would close or backorder against numbers we
                      // already know to be incomplete.
                      <span className="keying-summary">
                        {held ? 'Resolve the cause, then re-scan.' : 'For information only.'}
                      </span>
                    ) : canDecide ? (
                      <div className="toolbar">
                        {DECISIONS.map((d) => (
                          <button
                            key={d.key}
                            type="button"
                            title={d.hint}
                            className={d.key === 'hold' ? 'danger' : undefined}
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ doNo: r.do_no, decision: d.key })}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="keying-summary">
                        Needs someone who can approve write-backs.
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}
